import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Modal, Skeleton, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  getAccountComputeBalance,
  getAccountPaymentEntitlement,
  listAccountComputeRecords,
  type AccountComputeBalance,
  type AccountComputeRecord,
  type AccountPaymentEntitlement,
} from '../../api';
import { useAppStore } from '../../store';
import { ACCOUNT_PAYMENT_DATA_UPDATED_EVENT } from '../../utils/accountPaymentRefresh';

interface ComputePowerDetailModalProps {
  open: boolean;
  onClose: () => void;
  onBuyComputePower?: () => void;
  onOpenRules?: () => void;
}

const COMPUTE_RECORD_PAGE_SIZE = 10;
const COMPUTE_RECORD_SCROLL_THRESHOLD = 48;

function formatCompute(value?: number | string) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return Math.max(0, numeric).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatSignedCompute(value?: number | string) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return numeric.toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatRecordTime(value?: string | null) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const pad = (input: number) => String(input).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatExpireDate(value?: string | null) {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }

  return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`;
}

function resolveRecordTitle(record: AccountComputeRecord) {
  const displayRemark = (record.displayRemark || record.remark || '').trim();
  if (displayRemark) {
    return displayRemark;
  }
  if (record.eventType === 'TASK_REFUND') {
    return '任务失败返还';
  }
  if (record.eventType === 'RECHARGE') {
    return '算力豆购买';
  }
  if (record.eventType === 'ADMIN_GRANT') {
    return '账户算力调整';
  }
  if (record.eventType === 'REFUND_REVERSAL') {
    return '退款算力扣回';
  }
  return record.computeDelta >= 0 ? '算力豆到账' : '算力豆消耗';
}

export const ComputePowerDetailModal: React.FC<ComputePowerDetailModalProps> = ({
  open,
  onClose,
  onBuyComputePower,
  onOpenRules,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState<AccountComputeBalance | null>(null);
  const [entitlement, setEntitlement] = useState<AccountPaymentEntitlement | null>(null);
  const [records, setRecords] = useState<AccountComputeRecord[]>([]);
  const [recordPage, setRecordPage] = useState(1);
  const [recordTotal, setRecordTotal] = useState(0);
  const [recordsLoadingMore, setRecordsLoadingMore] = useState(false);
  const appName = useAppStore((state) => state.appConfig.appName);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [balancePayload, entitlementPayload, recordPayload] = await Promise.all([
        getAccountComputeBalance(),
        getAccountPaymentEntitlement().catch(() => null),
        listAccountComputeRecords({ page: 1, size: COMPUTE_RECORD_PAGE_SIZE }),
      ]);
      setBalance(balancePayload || null);
      setEntitlement(entitlementPayload || null);
      setRecords(recordPayload?.list || []);
      setRecordPage(1);
      setRecordTotal(recordPayload?.total || 0);
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '算力豆信息加载失败');
      setBalance(null);
      setEntitlement(null);
      setRecords([]);
      setRecordPage(1);
      setRecordTotal(0);
    } finally {
      setLoading(false);
      setRecordsLoadingMore(false);
    }
  }, [messageApi]);

  const loadMoreRecords = useCallback(async () => {
    if (loading || recordsLoadingMore || records.length >= recordTotal) {
      return;
    }

    const nextPage = recordPage + 1;
    setRecordsLoadingMore(true);
    try {
      const payload = await listAccountComputeRecords({
        page: nextPage,
        size: COMPUTE_RECORD_PAGE_SIZE,
      });
      setRecords((current) => [...current, ...(payload?.list || [])]);
      setRecordPage(nextPage);
      setRecordTotal(payload?.total || recordTotal);
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '算力豆明细加载失败');
    } finally {
      setRecordsLoadingMore(false);
    }
  }, [loading, messageApi, recordPage, recordTotal, records.length, recordsLoadingMore]);

  useEffect(() => {
    if (open) {
      void loadData();
    }
  }, [loadData, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePaymentDataUpdated = () => {
      void loadData();
    };

    window.addEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
    return () => {
      window.removeEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
    };
  }, [loadData, open]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton active paragraph={{ rows: 5 }} />;
    }

    return (
      <div className="main-layout__compute-meta">
        <div className="main-layout__compute-profile">
          <div className="main-layout__compute-avatar">N</div>
          <div>
            <strong>{appName}</strong>
            <span>
              {entitlement?.active
                ? `${entitlement.planName || '当前会员'} · 有效期至 ${formatExpireDate(entitlement.effectiveUntil)}`
                : '暂未开通会员'}
            </span>
          </div>
        </div>
        <section className="main-layout__compute-stats" aria-label="算力豆账户概览">
          <div className="main-layout__compute-balance">
            <strong>{formatCompute(balance?.balance)}</strong>
            <span>当前算力</span>
          </div>
          <div>
            <strong>{formatCompute(balance?.totalConsumed)}</strong>
            <span>累计消耗</span>
          </div>
          <div>
            <strong>{formatCompute(balance?.totalRecharged)}</strong>
            <span>累计到账</span>
          </div>
        </section>
      </div>
    );
  };

  const renderRecords = () => (
    <section
      className="main-layout__compute-records"
      aria-label="算力豆明细"
      onScroll={(event) => {
        const target = event.currentTarget;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom <= COMPUTE_RECORD_SCROLL_THRESHOLD) {
          void loadMoreRecords();
        }
      }}
    >
      {records.length ? (
        <>
          {records.map((record) => {
            const positive = record.computeDelta >= 0;
            return (
              <article key={record.recordNo || record.id} className="main-layout__compute-record">
                <div>
                  <strong>{resolveRecordTitle(record)}</strong>
                  <time>{formatRecordTime(record.createdAt)}</time>
                </div>
                <span className={positive ? 'main-layout__compute-record-amount--plus' : ''}>
                  {positive ? '+' : ''}{formatSignedCompute(record.computeDelta)}
                </span>
              </article>
            );
          })}
          {recordsLoadingMore ? (
            <div className="main-layout__compute-record-loading">
              <Spin size="small" />
            </div>
          ) : null}
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无算力豆明细" />
      )}
    </section>
  );

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        centered
        closable={false}
        footer={null}
        width={655}
        destroyOnHidden
        maskClosable
        onCancel={onClose}
        className="main-layout__compute-modal"
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div className="main-layout__compute-shell">
          <header className="main-layout__compute-header">
            <h3>算力豆详情</h3>
            <div className="main-layout__compute-actions">
              <Button
                type="primary"
                className="main-layout__compute-buy"
                onClick={onBuyComputePower}
              >
                购买算力豆
              </Button>
              <Button
                type="text"
                className="main-layout__compute-close"
                icon={<CloseOutlined />}
                aria-label="关闭算力豆详情弹窗"
                onClick={onClose}
              />
            </div>
          </header>
          {renderContent()}
          {!loading ? renderRecords() : null}
          <p className="main-layout__compute-note">
            仅展示近1个月的算力豆明细，数据更新可能有延迟
            <Button
              type="link"
              className="main-layout__compute-rule-link"
              onClick={onOpenRules}
            >
              算力豆规则
            </Button>
          </p>
        </div>
      </Modal>
    </>
  );
};
